/**
 * DiffSummary Component
 * 
 * Displays a compact summary of code changes in +/- format.
 * Example: "+1526 -638" style diff display.
 * Part of the json-render component system.
 */

import { Plus, Minus, FileCode2 } from 'lucide-react';

export interface DiffSummaryProps {
    /** Number of lines added */
    additions: number;
    /** Number of lines deleted */
    deletions: number;
    /** Number of files changed */
    filesChanged?: number;
    /** Optional title/context */
    title?: string;
    /** Size reduction in bytes (for image optimization) */
    bytesReduced?: number;
    /** Show compact inline style */
    compact?: boolean;
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DiffSummary({
    additions,
    deletions,
    filesChanged,
    title,
    bytesReduced,
    compact = false,
}: DiffSummaryProps) {
    if (compact) {
        return (
            <div className="flex items-center gap-2 text-sm font-mono">
                <span className="text-green-600 flex items-center gap-0.5">
                    <Plus className="h-3 w-3" />
                    {additions.toLocaleString()}
                </span>
                <span className="text-red-500 flex items-center gap-0.5">
                    <Minus className="h-3 w-3" />
                    {deletions.toLocaleString()}
                </span>
                {filesChanged !== undefined && (
                    <span className="text-muted-foreground flex items-center gap-1">
                        <FileCode2 className="h-3 w-3" />
                        {filesChanged} file{filesChanged !== 1 ? 's' : ''}
                    </span>
                )}
            </div>
        );
    }

    return (
        <div className="rounded-lg border bg-card p-3 space-y-2">
            {title && (
                <h4 className="text-sm font-medium text-muted-foreground">{title}</h4>
            )}

            <div className="flex items-center gap-4 font-mono text-sm">
                <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-green-500/20 border border-green-500/40 flex items-center justify-center">
                        <Plus className="h-2 w-2 text-green-600" />
                    </div>
                    <span className="text-green-600 font-semibold">
                        +{additions.toLocaleString()}
                    </span>
                </div>

                <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-red-500/20 border border-red-500/40 flex items-center justify-center">
                        <Minus className="h-2 w-2 text-red-500" />
                    </div>
                    <span className="text-red-500 font-semibold">
                        -{deletions.toLocaleString()}
                    </span>
                </div>

                {filesChanged !== undefined && (
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                        <FileCode2 className="h-4 w-4" />
                        <span>{filesChanged} file{filesChanged !== 1 ? 's' : ''}</span>
                    </div>
                )}
            </div>

            {bytesReduced !== undefined && bytesReduced > 0 && (
                <div className="text-xs text-muted-foreground">
                    📦 {formatBytes(bytesReduced)} saved
                </div>
            )}
        </div>
    );
}
